import React, { useContext, useEffect, useState, useRef } from "react";
import { kaReducer, Table } from "ka-table";
import { updateCellValue, deleteRow } from "ka-table/actionCreators";
import { DataType, EditingMode, PagingPosition } from "ka-table/enums";
import { FaUpload, FaTrash, FaCheck } from "react-icons/fa";
import { Box, Button, Tooltip, Typography, Menu, MenuItem, ListItemIcon, ListItemText, IconButton, Divider, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import MediacenterDialogueConfig from "./MediaCenterDiologueConfig";
import AlertDelete from "./AlertDelete";
import EmptyContainer from "../mediaCenter/emptyContainer/EmptyContainer";
import { useSnackbar } from "notistack";
import { search } from "ka-table/actionCreators";
import SearchBar from "./seacrhComponent/SearchBar";
import { IoFunnel } from "react-icons/io5";
import { Delete, PlayArrow, Pause, Edit, Save, Star, StarBorder, TextFields, Cloud, CloudUpload, Warning, Sync, Refresh, MoreVert, RestartAlt } from "@mui/icons-material";
import Chip from "@mui/material/Chip";
import "../mediaCenter/Demo.scss";
import { useMaterialUIController } from "context";
import InputBox from "./InputBox";
import DropDown from "./DropDown";
import { useGetLanguagesMutation } from "features/mediaCenterApiSlice";
import { v4 as uuidv4 } from "uuid";
import { useGetAllMediaCenterJsonMutation } from "features/mediaCenterApiSlice";
import { useSaveMediaCenterJsonMutation } from "features/mediaCenterApiSlice";
import { useSaveMP3FileMutation } from "features/mediaCenterApiSlice";
import CircularProgress from '@mui/material/CircularProgress';
import { appServices } from "globals/endpoints";
import { useDeleteAudioFileMutation } from "features/mediaCenterApiSlice";
import { useSyncSoundFilesMutation } from "features/mediaCenterApiSlice";
import { useRetryTTSMutation } from "features/mediaCenterApiSlice";
import { useGetFailedTTSItemsQuery } from "features/mediaCenterApiSlice";
import { useResetMediaCenterMutation } from "features/mediaCenterApiSlice";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const DEFAULT_LANGUAGE_KEY = "default";

const extractMediaCenterConfig = (response) => {
  const payload = response?.data?.data;
  if (!payload) {
    return { headerItems: [], languageSelected: [] };
  }

  const config = payload.config && typeof payload.config === "object" ? payload.config : payload;
  return {
    headerItems: Array.isArray(config.headerItems) ? config.headerItems : [],
    languageSelected: Array.isArray(config.languageSelected) ? config.languageSelected : [],
  };
};



const CustomUploader = ({ column, rowKeyValue, dispatch, rowData, currentlyPlayingId, onAudioPlay, isRowEditing, failedTTSItems, setFailedTTSItems, onRowsUpdate }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { darkMode } = useMaterialUIController()[0];
  const [file, setFile] = useState(null);
  const [saveMP3File] = useSaveMP3FileMutation();
  const [saveMediaCenterJson] = useSaveMediaCenterJsonMutation();
  const [getAllMediaCenterJson] = useGetAllMediaCenterJsonMutation();
  const [isPlaying, setIsPlaying] = useState(false)
  const [audio, setAudio] = useState(null);
  const [audioId, setAudioId] = useState(null);
  const [localAudioFileId, setLocalAudioFileId] = useState("");
  const [localAudioFileName, setLocalAudioFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isGeneratingTTS, setIsGeneratingTTS] = useState(false);
  const [deleteAudioFile] = useDeleteAudioFileMutation();
  const [audioText, setAudioText] = useState("");
  const [showEditTextDialog, setShowEditTextDialog] = useState(false);
  const [tempEditText, setTempEditText] = useState("");
  const isTextInitialized = useRef(false);
  const isTTSEnabled = String(AuthenticationService.getTTS()).toLowerCase() === "true";

  // Determine if upload should be shown based on requireLanguageWiseFiles flag
  const requireLanguageWiseFiles = rowData.requireLanguageWiseFiles !== undefined ? rowData.requireLanguageWiseFiles : true;
  const isDefaultColumn = column.key === DEFAULT_LANGUAGE_KEY;

  // Resolve property for current column; for non-language-wise rows, fallback to
  // any available audio metadata so play/status stays visible across columns.
  const property = rowData.properties.find(prop => prop.language === column.key);
  const sharedProperty = !requireLanguageWiseFiles
    ? rowData.properties.find(prop => prop.audioFileId || prop.audioFileName)
    : null;
  const resolvedProperty = (!property?.audioFileId && !property?.audioFileName && sharedProperty)
    ? sharedProperty
    : property;

  const audioFileId = resolvedProperty ? resolvedProperty.audioFileId : "";
  const audioFileName = resolvedProperty ? resolvedProperty.audioFileName : "";
  const text = resolvedProperty ? resolvedProperty.text : "";
  const isServerGenerated = resolvedProperty ? resolvedProperty.isServerGenerated : false;

  const shouldShowUploadInColumn = requireLanguageWiseFiles ? !isDefaultColumn : isDefaultColumn;

  const syncDefaultPropertyForSharedFile = (item) => {
    if (!item || item.requireLanguageWiseFiles !== false || !item.properties) {
      return item?.properties || [];
    }

    const sharedSource = item.properties.find(prop =>
      prop.language === DEFAULT_LANGUAGE_KEY || prop.audioFileId || prop.audioFileName
    );

    if (!sharedSource) {
      return item.properties;
    }

    const defaultIndex = item.properties.findIndex(prop => prop.language === DEFAULT_LANGUAGE_KEY);
    if (defaultIndex === -1) {
      return item.properties;
    }

    const syncedProperties = [...item.properties];
    syncedProperties[defaultIndex] = {
      ...syncedProperties[defaultIndex],
      audioFileId: sharedSource.audioFileId || '',
      audioFileName: sharedSource.audioFileName || '',
      text: sharedSource.text || '',
      isServerGenerated: Boolean(sharedSource.isServerGenerated),
    };

    return syncedProperties;
  };

  useEffect(() => {
    // Always sync local state from props whenever they change
    setLocalAudioFileId(audioFileId);
    setLocalAudioFileName(audioFileName);
    setAudioText(text || "");
  }, [audioFileId, audioFileName, text]);

  const handleFileUpload = async (event) => {
    if (audio) {
      setIsPlaying(false);
      audio.pause();
      audio.currentTime = 0;
      const audioUrl = audio.src;
      setAudio(null);
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
      }
    }
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      setIsUploading(true);
      await processFileUpload(uploadedFile);
    }
  };

  const processFileUpload = async (uploadedFile) => {
    try {
      setIsUploading(true);

      // For system prompts, use storageName as fileId and "system" as language
      const isSystem = rowData.isSystemPrompt === true;
      const fileIdFromGroupAndPhrase = isSystem ? rowData.storageName : `${rowData.type}-${rowData.name}`;

      // Create FormData and append the file, language, and file ID (File Group + Prompt)
      // Use "system" for system prompts, "default" if requireLanguageWiseFiles is false, otherwise column.key
      const languageParam = isSystem ? 'system' : (requireLanguageWiseFiles ? column.key : 'default');
      // Create FormData and append the file, language, and file ID
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('language', languageParam);
      formData.append('fileId', fileIdFromGroupAndPhrase);
      formData.append('languageWiseFiles', requireLanguageWiseFiles ? 'true' : 'false');

      // Upload the file - backend handles JSON update (clears text, sets serverGenerated=false)
      const saveMP3Response = await saveMP3File({
        content: formData,
        path: '/ivr/upload'
      });
        
      // Handle RTK Query error response
      if (saveMP3Response.error) {
        const errorData = saveMP3Response.error;
        const errorMsg = errorData.data?.message || errorData.message || "Error uploading file";
        const errorDetail = errorData.data?.messageDetail || errorData.messageDetail || "";
        const fullErrorMessage = errorDetail ? `${errorMsg}\n${errorDetail}` : errorMsg;
        
        enqueueSnackbar(fullErrorMessage, {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 7000,
          style: { whiteSpace: 'pre-line' }
        });
        return;
      }
      
      if (!saveMP3Response || !saveMP3Response.data) {
        enqueueSnackbar("No response from server", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 5000,
        });
        return;
      }
      
      if (saveMP3Response.data.status === 'ERROR') {
        const errorMsg = saveMP3Response.data.message || "Error uploading file";
        const errorDetail = saveMP3Response.data.messageDetail;
        const fullErrorMessage = errorDetail ? `${errorMsg}\n${errorDetail}` : errorMsg;
        
        enqueueSnackbar(fullErrorMessage, {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 7000,
          style: { whiteSpace: 'pre-line' }
        });
        return;
      }
      
      if (saveMP3Response.data.status === 'OK') {
        // Re-fetch the JSON from backend to get the updated state
        // (backend already cleared text and set isServerGenerated=false)
        const allMediaCenterJson = await getAllMediaCenterJson({
          getDataUrl: "/ivr/get/mediaCenterJson"
        });

        if (allMediaCenterJson.data) {
          const { headerItems } = extractMediaCenterConfig(allMediaCenterJson);
          // Find the updated property from the server response
          const updatedItem = headerItems.find(item => item.id === rowKeyValue);
          if (updatedItem) {
            const updatedProp = requireLanguageWiseFiles
              ? updatedItem.properties.find(p => p.language === column.key)
              : (updatedItem.properties.find(p => p.language === DEFAULT_LANGUAGE_KEY)
                || updatedItem.properties.find(p => p.audioFileId || p.audioFileName));
            if (updatedProp) {
              setLocalAudioFileId(updatedProp.audioFileId || "");
              setLocalAudioFileName(updatedProp.audioFileName || "");
              setAudioText(updatedProp.text || "");
              dispatch(updateCellValue(rowKeyValue, column.key, updatedProp.audioFileId || ""));
            }
          }
          
          // Update parent component's rows state for real-time UI updates
          if (onRowsUpdate && headerItems) {
            const newRows = headerItems.map(item => ({
              id: item.id,
              type: item.type,
              name: item.name,
              storageName: item.storageName || '',
              isSystemPrompt: item.isSystemPrompt || false,
              requireLanguageWiseFiles: item.requireLanguageWiseFiles !== undefined ? item.requireLanguageWiseFiles : true,
              properties: syncDefaultPropertyForSharedFile({
                ...item,
                properties: item.properties.some(prop => prop.language === DEFAULT_LANGUAGE_KEY)
                  ? item.properties
                  : [{
                    id: uuidv4(),
                    language: DEFAULT_LANGUAGE_KEY,
                    audioFileId: '',
                    audioFileName: '',
                    text: '',
                    isServerGenerated: false
                  }, ...item.properties]
              })
            }));
            onRowsUpdate(newRows);
          }
        }

        enqueueSnackbar("File uploaded successfully", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      
      let errorMessage = "Error uploading file";
      let errorDetail = "";
      
      if (error?.response?.data?.messageDetail) {
        errorDetail = error.response.data.messageDetail;
        errorMessage = error.response.data.message || errorMessage;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorDetail = error.message;
      }
      
      const fullErrorMsg = errorDetail ? `${errorMessage}: ${errorDetail}` : errorMessage;
      
      enqueueSnackbar(fullErrorMsg, {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 6000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenEditTextDialog = () => {
    if (!isTTSEnabled) {
      return;
    }
    // Use temporary state for editing
    setTempEditText(audioText);
    setShowEditTextDialog(true);
  };

  const handleSaveEditedText = async () => {
    try {
      setIsGeneratingTTS(true);
      
      // Update audioText with the edited value
      setAudioText(tempEditText);
      
      // Get current MediaCenter JSON
      const allMediaCenterJson = await getAllMediaCenterJson({
        getDataUrl: "/ivr/get/mediaCenterJson"
      });

      const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);

      // Only update the text field - let the backend handle TTS generation and isServerGenerated
      const updatedHeaderItems = headerItems.map(item => {
        if (item.id === rowKeyValue) {
          return {
            ...item,
            properties: item.properties.map(prop =>
              prop.language === column.key
                ? { ...prop, text: tempEditText }
                : prop
            )
          };
        }
        return item;
      });

      // Save the updated JSON - backend will diff and generate TTS if text changed
      const saveResponse = await saveMediaCenterJson({
        path: "/ivr/save/mediaCenterJson",
        content: {
          languageSelected,
          headerItems: updatedHeaderItems
        }
      });

      // Update local state from the save response (which contains TTS-updated config)
      if (saveResponse.data && saveResponse.data.data) {
        const responseData = saveResponse.data.data;
        const updatedConfig = responseData.config;
        
        // Track failed TTS items from save response
        const failedItems = responseData.failedTTSItems || responseData.allFailedTTSItems || responseData.ttsFailedItems || [];
        setFailedTTSItems(failedItems); // Always update, even if empty
        
        if (updatedConfig && updatedConfig.headerItems) {
          const updatedItem = updatedConfig.headerItems.find(item => item.id === rowKeyValue);
          if (updatedItem) {
            const updatedProp = updatedItem.properties.find(p => p.language === column.key);
            if (updatedProp) {
              setLocalAudioFileId(updatedProp.audioFileId || "");
              setLocalAudioFileName(updatedProp.audioFileName || "");
              setAudioText(updatedProp.text || "");
            }
          }
          
          // Update parent component's rows state for real-time UI updates
          if (onRowsUpdate && updatedConfig.headerItems) {
            const newRows = updatedConfig.headerItems.map(item => ({
              id: item.id,
              type: item.type,
              name: item.name,
              storageName: item.storageName || '',
              isSystemPrompt: item.isSystemPrompt || false,
              requireLanguageWiseFiles: item.requireLanguageWiseFiles !== undefined ? item.requireLanguageWiseFiles : true,
              properties: item.properties.some(prop => prop.language === DEFAULT_LANGUAGE_KEY)
                ? item.properties
                : [{
                  id: uuidv4(),
                  language: DEFAULT_LANGUAGE_KEY,
                  audioFileId: '',
                  audioFileName: '',
                  text: '',
                  isServerGenerated: false
                }, ...item.properties]
            }));
            onRowsUpdate(newRows);
          }
        }

        // Show TTS results if applicable
        const ttsSuccessCount = responseData.ttsSuccessCount || 0;
        const ttsFailedCount = responseData.ttsFailedCount || 0;

        if (ttsSuccessCount > 0 && ttsFailedCount === 0) {
          enqueueSnackbar(`Text saved & audio generated successfully`, {
            variant: "success",
            anchorOrigin: { horizontal: "right", vertical: "bottom" },
            autoHideDuration: 3000,
          });
        } else if (ttsFailedCount > 0) {
          enqueueSnackbar(`Text saved. TTS: ${ttsSuccessCount} generated, ${ttsFailedCount} failed`, {
            variant: "warning",
            anchorOrigin: { horizontal: "right", vertical: "bottom" },
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar("Text updated successfully", {
            variant: "success",
            anchorOrigin: { horizontal: "right", vertical: "bottom" },
            autoHideDuration: 3000,
          });
        }
      } else {
        enqueueSnackbar("Text updated successfully", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 3000,
        });
      }

      setShowEditTextDialog(false);
    } catch (error) {
      console.error('Error updating text:', error);
      
      // Extract detailed error message from API response
      let errorMessage = "Error updating text";
      let errorDetail = "";
      
      if (error?.data?.messageDetail) {
        errorDetail = error.data.messageDetail;
        errorMessage = error.data.message || errorMessage;
      } else if (error?.message) {
        errorDetail = error.message;
      } else if (typeof error === 'string') {
        errorDetail = error;
      }
      
      const fullErrorMsg = errorDetail ? `${errorMessage}: ${errorDetail}` : errorMessage;
      
      enqueueSnackbar(fullErrorMsg, {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 6000,
      });
    } finally {
      setIsGeneratingTTS(false);
      setShowEditTextDialog(false); // Close dialog on both success and failure
    }
  };

  const handlePlayPauseAudio = async () => {
    if (localAudioFileId) {
      // If audio is currently playing, pause it
      if (isPlaying && audio) {
        audio.pause();
        setIsPlaying(false);
        return;
      }
      
      // Clean up any existing audio before fetching fresh
      if (audio) {
        audio.pause();
        const audioUrl = audio.src;
        if (audioUrl && audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
        setAudio(null);
      }
      
      // Always fetch fresh audio from server
      setIsLoadingAudio(true);
      setAudioId(localAudioFileId);
      onAudioPlay(localAudioFileId);
      
      try {
        // Fetch audio file directly without RTK Query
        // Use "system" for system prompts, "default" if requireLanguageWiseFiles is false, otherwise column.key
        const isSystem = rowData.isSystemPrompt === true;
        const languageParam = isSystem ? 'system' : (requireLanguageWiseFiles ? column.key : 'default');
        const response = await appServices({
          url: `/auth/mediaCenter/play/${languageParam}/${localAudioFileName}`,
          method: 'GET',
          responseType: 'arraybuffer'
        });
        
        if (response.data) {
          const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
          const url = URL.createObjectURL(audioBlob);
          const audioObject = new Audio(url);
          audioObject.setAttribute('data-audio-id', localAudioFileId);
          
          audioObject.addEventListener('ended', () => {
            setIsPlaying(false);
            // Clean up after playback ends
            const audioUrl = audioObject.src;
            if (audioUrl && audioUrl.startsWith('blob:')) {
              URL.revokeObjectURL(audioUrl);
            }
            setAudio(null);
          });
          
          setAudio(audioObject);
          await audioObject.play();
          setIsPlaying(true);
          setIsLoadingAudio(false);
        }
      } catch (error) {
        console.error('Error loading audio:', error);
        setIsLoadingAudio(false);
        enqueueSnackbar("Error loading audio file", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 3000,
        });
      }
    }
  }

  useEffect(() => {
    if (currentlyPlayingId !== audioId && isPlaying) {
      audio?.pause();
      setIsPlaying(false);
    }
  }, [currentlyPlayingId, audioId, isPlaying, audio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audio) {
        audio.pause();
        const audioUrl = audio.src;
        if (audioUrl && audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
      }
    };
  }, [audio]);

  const handleDeleteAudio = async () => {
    if (audio) {
      setIsPlaying(false);
      audio.pause();
      audio.currentTime = 0;
      const audioUrl = audio.src;
      setAudio(null);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    }

    try {
      // Delete the audio file
      // Use "default" as language if requireLanguageWiseFiles is false (single file for all languages)
      // Delete the audio file - language should be the language name (column key), not the audioFileId
      await deleteAudioFile({
        path: "/ivr/deleteFile",
        content: {
          language: column.key || 'default',
          fileName: localAudioFileName
        }
      });

      // Get current MediaCenter JSON
      const allMediaCenterJson = await getAllMediaCenterJson({
        getDataUrl: "/ivr/get/mediaCenterJson"
      });

      const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);

      // Update the specific item in headerItems
      const updatedHeaderItems = headerItems.map(item => {
        if (item.id === rowKeyValue) {
          return {
            ...item,
            properties: item.properties.map(prop =>
              prop.language === column.key
                ? { ...prop, audioFileId: '', audioFileName: '', text: '', isServerGenerated: false }
                : prop
            )
          };
        }
        return item;
      });

      // Save the updated MediaCenter JSON
      await saveMediaCenterJson({
        path: "/ivr/save/mediaCenterJson",
        content: {
          languageSelected,
          headerItems: updatedHeaderItems
        }
      });

      // Update local state
      setLocalAudioFileId('');
      setLocalAudioFileName('');
      setAudioFilePath('');
      setAudioText('');

      // Update parent component's rows state for real-time UI updates
      if (onRowsUpdate && updatedHeaderItems) {
        const newRows = updatedHeaderItems.map(item => ({
          id: item.id,
          type: item.type,
          name: item.name,
          requireLanguageWiseFiles: item.requireLanguageWiseFiles !== undefined ? item.requireLanguageWiseFiles : true,
          properties: item.properties.some(prop => prop.language === DEFAULT_LANGUAGE_KEY)
            ? item.properties
            : [{
              id: uuidv4(),
              language: DEFAULT_LANGUAGE_KEY,
              audioFileId: '',
              audioFileName: '',
              text: '',
              isServerGenerated: false
            }, ...item.properties]
        }));
        onRowsUpdate(newRows);
      }

      enqueueSnackbar("Audio file deleted successfully", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Error deleting audio file:', error);
      
      let errorMessage = "Error deleting audio file";
      let errorDetail = "";
      
      if (error?.data?.messageDetail) {
        errorDetail = error.data.messageDetail;
        errorMessage = error.data.message || errorMessage;
      } else if (error?.response?.data?.messageDetail) {
        errorDetail = error.response.data.messageDetail;
        errorMessage = error.response.data.message || errorMessage;
      } else if (error?.message) {
        errorDetail = error.message;
      }
      
      const fullErrorMsg = errorDetail ? `${errorMessage}: ${errorDetail}` : errorMessage;
      
      enqueueSnackbar(fullErrorMsg, {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 6000,
      });
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div className="custom-editor">
      <div className="uploaded-file" style={{ display: "flex", gap: "10px", alignItems: "center", paddingLeft: "10px" }}>
        {shouldShowUploadInColumn && (
          <div className="upload-container" >
            <input
              type="file"
              id={`fileInput-${rowKeyValue}-${column.key}`}
              accept="audio/mp3, audio/wav"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            <Tooltip title={localAudioFileId ? "Re-Upload Audio" : "Upload Audio"}>
              <label
                htmlFor={`fileInput-${rowKeyValue}-${column.key}`}
                className="upload-button"
                style={{ cursor: "pointer" }}
              >
                {isUploading ? (
                  <CircularProgress size={20} />
                ) : (
                  <FaUpload  style={{ color: localAudioFileId ? "green" : "inherit" }} />
                )}
              </label>
            </Tooltip>
          </div>
        )}

        {isTTSEnabled && (
          <Tooltip title={audioText ? `Text: ${audioText}` : "Add/Edit Text"}>
            <TextFields
              onClick={handleOpenEditTextDialog}
              sx={{
                fontSize: '1rem',
                color: audioText ? '#4caf50' : '#9e9e9e',
                marginLeft: '4px',
                cursor: 'pointer',
                '&:hover': {
                  color: audioText ? '#45a049' : '#757575'
                }
              }}
            />
          </Tooltip>
        )}


        {/* Show warning if this item is in the failed TTS list */}
        {isTTSEnabled && (() => {
          const isFailedItem = failedTTSItems.some(failedItem => 
            failedItem.headerItemId === rowKeyValue && 
            failedItem.language === column.key
          );
          return isFailedItem ? (
            <Tooltip title={`TTS Failed: ${failedTTSItems.find(f => f.headerItemId === rowKeyValue && f.language === column.key)?.failureReasons?.[0] || 'Unknown error'}`}>
              <Warning 
                sx={{ 
                  fontSize: '1rem', 
                  color: '#f44336',
                  marginLeft: '4px'
                }} 
              />
            </Tooltip>
          ) : null;
        })()}

        {localAudioFileId && (
          <>
            {isLoadingAudio ? (
              <CircularProgress size={20} />
            ) : (
              <Tooltip title={isPlaying ? "Pause Audio" : "Play Audio"}>
                {isPlaying ? (
                  <Pause fontSize="small" style={{ cursor: "pointer", color: "green",marginBottom:"3px" }} onClick={handlePlayPauseAudio} />
                ) : (
                  <PlayArrow fontSize="small" style={{ cursor: "pointer", color: "green" ,marginBottom:"3px" }} onClick={handlePlayPauseAudio} />
                )}
              </Tooltip>
            )}
            {localAudioFileName && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: localAudioFileName.endsWith('.mp3') ? '#1976d2' : '#ed6c02',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    marginLeft: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px'
                  }}
                >
                  {isServerGenerated && (
                    <Tooltip title="Server-generated (TTS)">
                      <Cloud sx={{ fontSize: '0.7rem' }} />
                    </Tooltip>
                  )}
                  {!isServerGenerated && (
                    <Tooltip title="User content">
                      <CloudUpload sx={{ fontSize: '0.7rem' }} />
                    </Tooltip>
                  )}
                  {localAudioFileName.split('.').pop().toUpperCase()}
                </Typography>
              </>
            )}
            {isRowEditing && !rowData.isSystemPrompt && (
              <Tooltip title="Delete Audio">
                <Delete
                  fontSize="small"
                  style={{ cursor: "pointer", color: "red",marginBottom:"5px" }}
                  onClick={() => setShowDeleteConfirm(true)}
                />
              </Tooltip>
            )}
          </>
        )}
      </div>
      {showDeleteConfirm && (
        <AlertDelete
          name="audio file"
          handleConfirmDelete={() => {
            handleDeleteAudio();
            setShowDeleteConfirm(false);
          }}
          isDelete={showDeleteConfirm}
          setIsDelete={setShowDeleteConfirm}
        />
      )}
      
      {/* Edit Text Dialog */}
      {isTTSEnabled && showEditTextDialog && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {
            setShowEditTextDialog(false);
            // Don't reset text - just close the dialog
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
              padding: '32px',
              borderRadius: '12px',
              minWidth: '450px',
              maxWidth: '650px',
              boxShadow: darkMode 
                ? '0 24px 48px rgba(0, 0, 0, 0.6), 0 12px 24px rgba(0, 0, 0, 0.4)'
                : '0 24px 48px rgba(0, 0, 0, 0.3), 0 12px 24px rgba(0, 0, 0, 0.2)',
              border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: darkMode ? '#ffffff' : '#1a1a1a', fontWeight: 600 }}>
              {tempEditText ? 'Edit Text' : 'Add Text'}
            </Typography>
            <Box sx={{ mb: 3, p: 2, backgroundColor: darkMode ? '#121212' : '#f5f5f5', borderRadius: '6px', border: darkMode ? '1px solid #404040' : '1px solid #e0e0e0' }}>
              <Typography variant="body2" sx={{ color: darkMode ? '#b0b0b0' : '#666666', lineHeight: 1.6 }}>
                <strong style={{ color: '#1976d2' }}>Language:</strong> {column.key}
                {' | '}
                <strong style={{ color: '#1976d2' }}>File:</strong> {localAudioFileName}
              </Typography>
            </Box>
            <textarea
              value={tempEditText}
              onChange={(e) => setTempEditText(e.target.value)}
              placeholder="Enter text here..."
              autoFocus
              style={{
                width: '100%',
                minHeight: '140px',
                padding: '12px',
                borderRadius: '6px',
                border: darkMode ? '2px solid #404040' : '2px solid #e0e0e0',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: darkMode ? '#121212' : '#fafafa',
                color: darkMode ? '#ffffff' : '#1a1a1a'
              }}
              onFocus={(e) => e.target.style.borderColor = '#1976d2'}
              onBlur={(e) => e.target.style.borderColor = darkMode ? '#404040' : '#e0e0e0'}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowEditTextDialog(false);
                  // Discard edits - don't update audioText
                }}
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEditedText}
                disabled={isGeneratingTTS}
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  },
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#999'
                  }
                }}
              >
                {isGeneratingTTS ? (
                  <>
                    <CircularProgress size={20} sx={{ color: '#ffffff', mr: 1 }} />
                    Generating TTS...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

const DeleteRowComponent = ({
  rowKeyValue,
  deleteItemName,
  isEditing,
  isSystemPrompt,
  onEditClick,
  onSaveClick,
  onDeleteClick
}) => {
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      {!isSystemPrompt && (
        <Tooltip title="Delete">
          <Delete
            fontSize="small"
            sx={{ cursor: "pointer", color: "red" }}
            onClick={() => onDeleteClick(rowKeyValue, deleteItemName)}
          />
        </Tooltip>
      )}
    </div>
  );
};

const MediaCenterComponent = () => {
  const [columnName, setColumnName] = useState("");
  const [columns, setColumns] = useState([
    { key: "type", title: "", width: 90 },
    {
      key: "name",
      title: "Prompt",
      width: 80,
      isEditable: true,
      dataType: DataType.String
    },
    { key: DEFAULT_LANGUAGE_KEY, title: "Neutral", width: 90 },
    { key: ":delete", width: 90, title: "Action" },
  ]);
  const [rows, setRows] = useState([]);
  const [createName, setCreateName] = useState("");
  const [createGroup, setCreateGroup] = useState("");
  const [openCreateTableDiologue, setOpenCreateTableDiologue] = useState(false);
  const [closeColumnDiologueBox, setCloseColumnDiologueBox] = useState(false);
  const [isSelectExistingRow, setIsSelectExistingRow] = React.useState(false);
  const [isSelectNewRow, setIsSelectNewRow] = React.useState(false);
  const [isShowSearchBox, setIsShowSearchBox] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");
  const [deleteItemTitle, setDeleteItemTitle] = React.useState("");
  const [isEditColumnName, setIsEditColumnName] = React.useState(false);
  const { darkMode } = useMaterialUIController()[0];
  const [isConfirmDeleteColumn, setIsConfirmDeleteColumn] =
    React.useState(false);
  const [isConfirmDeleteRow, setIsConfirmDeleteRow] = React.useState(false);
  const [deleteRowDetails, setDeleteRowDetails] = React.useState({});
  const [editedColumnName, setEditedColumnName] = React.useState("");
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);
  const [languageList, setLanguageList] = useState([])
  const [requireLanguageWiseFiles, setRequireLanguageWiseFiles] = useState(true);


  const [getLanguages] = useGetLanguagesMutation()
  const [getAllMediaCenterJson] = useGetAllMediaCenterJsonMutation()
  const [saveMediaCenterJson] = useSaveMediaCenterJsonMutation()
  const [deleteAudioFile] = useDeleteAudioFileMutation();
  const [syncSoundFiles] = useSyncSoundFilesMutation();
  const [retryTTS] = useRetryTTSMutation();
  const [resetMediaCenter] = useResetMediaCenterMutation();
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRetryingTTS, setIsRetryingTTS] = useState(false);
  const [failedTTSItems, setFailedTTSItems] = useState([]);
  const [loadingFailedItems, setLoadingFailedItems] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const isAdmin = true;
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const isMoreMenuOpen = Boolean(moreMenuAnchorEl);

  const [groupFilter, setGroupFilter] = useState("all"); // "all" | "IVR" | "MOH" | "Others"
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedName, setEditedName] = useState("");

  const ensureDefaultProperty = (properties = []) => {
    if (properties.some(prop => prop.language === DEFAULT_LANGUAGE_KEY)) {
      return properties;
    }

    return [
      {
        id: uuidv4(),
        language: DEFAULT_LANGUAGE_KEY,
        audioFileId: "",
        audioFileName: "",
        text: "",
        isServerGenerated: false,
      },
      ...properties,
    ];
  };

  const handleEditClick = (rowId) => {
    const row = rows.find(r => r.id === rowId);
    setEditedName(row.name);
    setEditingRowId(rowId);
  };

  const handleSaveClick = async (rowId) => {
    try {
      const allMediaCenterJson = await getAllMediaCenterJson({
        getDataUrl: "/ivr/get/mediaCenterJson"
      });

      const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);

      // Update the name in headerItems
      const updatedHeaderItems = headerItems.map(item => {
        if (item.id === rowId) {
          return {
            ...item,
            name: editedName
          };
        }
        return item;
      });

      // Save the updated JSON
      await saveMediaCenterJson({
        path: "/ivr/save/mediaCenterJson",
        content: {
          languageSelected,
          headerItems: updatedHeaderItems
        }
      });

      // Update local state
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === rowId ? { ...row, name: editedName } : row
        )
      );

      dispatch(updateCellValue(rowId, 'name', editedName));
      setEditingRowId(null);
      setEditedName("");

      enqueueSnackbar("Name updated successfully", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Error updating name:', error);
      enqueueSnackbar("Error updating name", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 3000,
      });
    }
  };

  const handleSyncSoundFiles = async () => {
    try {
      setIsSyncing(true);
      const response = await syncSoundFiles();
      
      if (response.error) {
        const errorMsg = response.error.data?.messageDetail || response.error.message || "Error synchronising media files";
        enqueueSnackbar(errorMsg, {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 6000,
        });
      } else {
        enqueueSnackbar("Media files synchronised successfully", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error('Error synchronising sound files:', error);
      
      let errorMessage = "Error synchronising media files";
      let errorDetail = "";
      
      if (error?.data?.messageDetail) {
        errorDetail = error.data.messageDetail;
        errorMessage = error.data.message || errorMessage;
      } else if (error?.message) {
        errorDetail = error.message;
      }
      
      const fullErrorMsg = errorDetail ? `${errorMessage}: ${errorDetail}` : errorMessage;
      
      enqueueSnackbar(fullErrorMsg, {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 6000,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Reset Media Center - destructive operation
  const handleResetMediaCenter = async () => {
    try {
      setIsResetting(true);
      const username = window.sessionStorage.getItem(
        `${window.location.hostname}-coral-X-Username`
      ) || "admin";

      const response = await resetMediaCenter({
        content: { performedBy: username },
      });

      if (response.error) {
        const errorMsg =
          response.error.data?.messageDetail ||
          response.error.message ||
          "Error resetting media center";
        enqueueSnackbar(errorMsg, {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 6000,
        });
      } else {
        enqueueSnackbar("Media Center has been reset successfully", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 3000,
        });
        // Fully reload all state from server after reset
        setRows([]);
        setFailedTTSItems([]);
        setColumns([]);
        setEditingRowId(null);
        setEditedName("");
        setSearchText("");
        setGroupFilter("all");

        const allMediaCenterJson = await getAllMediaCenterJson({
          getDataUrl: "/ivr/get/mediaCenterJson",
        });

        if (allMediaCenterJson.data) {
          const responseData = allMediaCenterJson.data.data;
          const { failedTTSItems: freshFailedItems } = responseData || {};
          const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);

          setFailedTTSItems(freshFailedItems || []);

          const newRows = headerItems.map((item) => ({
            id: item.id,
            type: item.type,
            name: item.name,
            storageName: item.storageName || '',
            isSystemPrompt: item.isSystemPrompt || false,
            requireLanguageWiseFiles:
              item.requireLanguageWiseFiles !== undefined
                ? item.requireLanguageWiseFiles
                : true,
            properties: ensureDefaultProperty(item.properties),
          }));
          setRows(newRows);

          // Rebuild columns from fresh server data
          const newColumns = [
            { key: "type", title: "", width: 90, dataType: DataType.String },
            { dataType: DataType.String, key: "name", title: "Prompt", width: 80 },
            { key: DEFAULT_LANGUAGE_KEY, title: "Neutral", width: 90 },
            ...languageSelected
              .filter(lang => lang.language !== DEFAULT_LANGUAGE_KEY)
              .map(lang => ({
                key: lang.language,
                title: lang.language,
                width: 90
              })),
            { key: ":delete", width: 90, title: "Actions" }
          ];

          const columnsWithDropdown = newColumns.map((item) => {
            if (item.key !== "name" && item.key !== ":delete" && item.key !== "type" && item.key !== DEFAULT_LANGUAGE_KEY) {
              const isDefaultLanguage = languageSelected.find(lang =>
                lang.language === item.key
              )?.isDefault;
              return {
                ...item,
                title: (
                  <Box key={item.key} sx={{
                    display: "flex",
                    gap: "5px",
                    textAlign: "center",
                    padding: '4px',
                    borderRadius: '4px'
                  }}>
                    {isDefaultLanguage && <Star fontSize="small" sx={{ color: 'gold' }} />}
                    <Typography variant="p">{item.title}</Typography>
                    <DropDown
                      handleDelete={() => handleDeleteColumn(item.key, item.title)}
                      handleSetDefault={() => handleSetDefaultLanguage(item.key)}
                      isDefault={isDefaultLanguage}
                    />
                  </Box>
                ),
              };
            }
            return item;
          });

          setColumns(columnsWithDropdown);

          // Reset tableProps completely with fresh data
          changeTableProps(prevState => ({
            ...prevState,
            data: newRows,
            columns: columnsWithDropdown,
            editableCells: newRows.flatMap(row =>
              columnsWithDropdown
                .filter(col => col.key !== "type" && col.key !== "name" && col.key !== ":delete")
                .map(col => ({ columnKey: col.key, rowKeyValue: row.id }))
            ),
            searchText: "",
          }));

          // Refresh available language list
          const getLanguageListResponse = await getLanguages({
            getDataUrl: "/ivr/languages"
          });
          if (getLanguageListResponse.data) {
            const filteredLanguageList = getLanguageListResponse.data.filter(item =>
              !languageSelected.some(lang => lang.language === item.language)
            );
            setLanguageList(filteredLanguageList);
          }
        } else {
          // Server returned empty — clear everything
          changeTableProps(prevState => ({
            ...prevState,
            data: [],
            columns: [],
            editableCells: [],
            searchText: "",
          }));
          setLanguageList([]);
        }
      }
    } catch (error) {
      console.error("Error resetting media center:", error);
      enqueueSnackbar("Error resetting media center", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 6000,
      });
    } finally {
      setIsResetting(false);
      setIsResetDialogOpen(false);
    }
  };

  // Retry TTS for items that have text but no audio file (failed TTS)
  const handleRetryTTS = async () => {
    try {
      setIsRetryingTTS(true);

      let itemsToRetry = [];
      
      // First, try to use the failedTTSItems we already have in state
      if (failedTTSItems && failedTTSItems.length > 0) {
        itemsToRetry = failedTTSItems.map(item => ({
          headerItemId: item.headerItemId,
          language: item.language
        }));
      } else {
        // If we don't have them in state, fetch the failed items from backend
        const allMediaCenterJson = await getAllMediaCenterJson({
          getDataUrl: "/ivr/get/mediaCenterJson"
        });

        if (!allMediaCenterJson.data || !allMediaCenterJson.data.data) {
          enqueueSnackbar("Unable to load media center data", {
            variant: "error",
            anchorOrigin: { horizontal: "right", vertical: "bottom" },
            autoHideDuration: 3000,
          });
          return;
        }

        const responseData = allMediaCenterJson.data.data;
        const failedItems = responseData.failedTTSItems || [];
        
        if (failedItems.length > 0) {
          itemsToRetry = failedItems.map(item => ({
            headerItemId: item.headerItemId,
            language: item.language
          }));
          // Update state with loaded failed items
          setFailedTTSItems(failedItems);
        }
      }

      if (itemsToRetry.length === 0) {
        enqueueSnackbar("No failed TTS items to retry", {
          variant: "info",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 3000,
        });
        return;
      }

      const response = await retryTTS({ content: itemsToRetry });

      if (response.error) {
        const errorMsg = response.error.data?.messageDetail || response.error.message || "TTS retry failed";
        enqueueSnackbar(errorMsg, {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
          autoHideDuration: 6000,
        });
      } else {
        const data = response.data?.data;
        const successCount = data?.ttsSuccessCount || 0;
        const failedCount = data?.ttsFailedCount || 0;

        if (failedCount === 0 && successCount > 0) {
          enqueueSnackbar(`TTS retry: ${successCount} audio file(s) generated successfully`, {
            variant: "success",
            anchorOrigin: { horizontal: "right", vertical: "bottom" },
            autoHideDuration: 3000,
          });
        } else if (failedCount > 0) {
          enqueueSnackbar(`TTS retry: ${successCount} succeeded, ${failedCount} failed`, {
            variant: "warning",
            anchorOrigin: { horizontal: "right", vertical: "bottom" },
            autoHideDuration: 5000,
          });
        }

        // Refresh data to show updated state
        const refreshedJson = await getAllMediaCenterJson({
          getDataUrl: "/ivr/get/mediaCenterJson"
        });
        if (refreshedJson.data && refreshedJson.data.data) {
          const refreshedData = refreshedJson.data.data;
          const { config, failedTTSItems: updatedFailedItems } = refreshedData;
          const { headerItems: refreshedItems } = config;
          
          // Update failed items tracking
          setFailedTTSItems(updatedFailedItems || []);
          
          const newRows = refreshedItems.map(item => ({
            id: item.id,
            type: item.type,
            name: item.name,
            storageName: item.storageName || '',
            isSystemPrompt: item.isSystemPrompt || false,
            requireLanguageWiseFiles: item.requireLanguageWiseFiles,
            ...ensureDefaultProperty(item.properties).reduce((acc, prop) => {
              acc[prop.language] = prop;
              return acc;
            }, {}),
            properties: ensureDefaultProperty(item.properties),
          }));
          setRows(newRows);
        }
      }
    } catch (error) {
      console.error('Error retrying TTS:', error);
      
      let errorMessage = "Error retrying TTS generation";
      let errorDetail = "";
      
      if (error?.data?.messageDetail) {
        errorDetail = error.data.messageDetail;
        errorMessage = error.data.message || errorMessage;
      } else if (error?.message) {
        errorDetail = error.message;
      } else if (typeof error === 'string') {
        errorDetail = error;
      }
      
      const fullErrorMsg = errorDetail ? `${errorMessage}: ${errorDetail}` : errorMessage;
      
      enqueueSnackbar(fullErrorMsg, {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 6000,
      });
    } finally {
      setIsRetryingTTS(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      const allMediaCenterJson = await getAllMediaCenterJson({
        getDataUrl: "/ivr/get/mediaCenterJson"
      });

      if (allMediaCenterJson.data) {
        const responseData = allMediaCenterJson.data.data;
        const { failedTTSItems, failedTTSCount } = responseData;
        const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);
        
        // Track failed TTS items
        setFailedTTSItems(failedTTSItems || []);
        if (failedTTSItems && failedTTSItems.length > 0) {
          console.warn(`Found ${failedTTSItems.length} failed TTS item(s):`, failedTTSItems);
        }

        const newRows = headerItems.map(item => ({
          id: item.id,
          type: item.type,
          name: item.name,
          storageName: item.storageName || '',
          isSystemPrompt: item.isSystemPrompt || false,
          requireLanguageWiseFiles: item.requireLanguageWiseFiles !== undefined ? item.requireLanguageWiseFiles : true,
          properties: ensureDefaultProperty(item.properties)
        }));

        setRows(newRows);

        const newColumns = [
          { key: "type", title: "", width: 90, dataType: DataType.String },
          { dataType: DataType.String, key: "name", title: "Prompt", width: 80 },
          { key: DEFAULT_LANGUAGE_KEY, title: "Neutral", width: 90 },
          ...languageSelected
            .filter(lang => lang.language !== DEFAULT_LANGUAGE_KEY)
            .map(lang => ({
            key: lang.language,
            title: lang.language,
            width: 90
          })),
          { key: ":delete", width: 90, title: "Actions" }
        ];

        // Add dropdown to columns
        const columnsWithDropdown = newColumns.map((item) => {
          if (item.key !== "name" && item.key !== ":delete" && item.key !== "type" && item.key !== DEFAULT_LANGUAGE_KEY) {
            const isDefaultLanguage = languageSelected.find(lang =>
              lang.language === item.key
            )?.isDefault;

            return {
              ...item,
              title: (
                <Box key={item.key} sx={{
                  display: "flex",
                  gap: "5px",
                  textAlign: "center",
                  padding: '4px',
                  borderRadius: '4px'
                }}>
                  {isDefaultLanguage && <Star fontSize="small" sx={{ color: 'gold' }} />}
                  <Typography variant="p">{item.title}</Typography>
                  <DropDown
                    handleDelete={() => handleDeleteColumn(item.key, item.title)}
                    handleSetDefault={() => handleSetDefaultLanguage(item.key)}
                    isDefault={isDefaultLanguage}
                  />
                </Box>
              ),
            };
          }
          return item;
        });

        setColumns(columnsWithDropdown);

        changeTableProps(prevState => ({
          ...prevState,
          data: newRows,
          columns: columnsWithDropdown,
          editableCells: newRows.flatMap(row =>
            columnsWithDropdown
              .filter(col => col.key !== "type" && col.key !== "name" && col.key !== ":delete")
              .map(col => ({ columnKey: col.key, rowKeyValue: row.id }))
          )
        }));
      }
      return allMediaCenterJson;
    }

    async function getLanguageList() {
      const masterData = await fetchData();
      const { languageSelected } = extractMediaCenterConfig(masterData);

      const getLanguageList = await getLanguages({
        getDataUrl: "/ivr/languages"
      });

      // Filter out languages that are already in the columns
      const languageData = getLanguageList?.data || [];
      const filteredLanguageList = languageData.filter(item =>
        !languageSelected.some(lang => lang.language === item.language)
      );

      setLanguageList(filteredLanguageList);
    }

    getLanguageList();
  }, []);

  // Filter rows based on groupFilter and sync to tableProps
  const getFilteredRows = (sourceRows, filter) => {
    if (filter === "all") return sourceRows;
    if (filter === "IVR") return sourceRows.filter(r => r.type === "IVR");
    if (filter === "MOH") return sourceRows.filter(r => r.type === "MOH");
    // "Others" = everything that is not IVR or MOH
    return sourceRows.filter(r => r.type !== "IVR" && r.type !== "MOH");
  };

  useEffect(() => {
    const filtered = getFilteredRows(rows, groupFilter);
    changeTableProps(prev => ({ ...prev, data: filtered }));
  }, [rows, groupFilter]);

  const [tableProps, changeTableProps] = useState({
    data: rows,
    columns: columns.map((item, idx) => {
      if (
        item.key !== "name" &&
        item.key !== ":delete" &&
        item.key !== "type" &&
        item.key !== "type" &&
        item.key !== DEFAULT_LANGUAGE_KEY
      ) {
        return {
          ...item,
          title: (
            <Box
              key={item.key}
              sx={{ display: "flex", gap: "5px", textAlign: "center" }}
            >
              <Typography variant="p">{item.title}</Typography>
              <DropDown
                handleDelete={() => handleDeleteColumn(item.key, item.title)}
                handleEditColumn={() =>
                  handleEditColumnName(item.key, item.title)
                }
              />
            </Box>
          ),
        };
      }
      return item;
    }),
    editableCells: rows.flatMap((item) => {
      return [
        { columnKey: DEFAULT_LANGUAGE_KEY, rowKeyValue: item.id },
      ];
    }),
    editingMode: EditingMode.None, // Change this from EditingMode.Cell
    rowKeyField: "id",

    search: ({ searchText, rowData, column }) => {
      if (column.key === "passed") {
        return (
          (searchText === "false" && !rowData.passed) ||
          (searchText === "true" && rowData.passed)
        );
      }
    },
    searchText: "",
  });
  const dispatch = (action) => {
    changeTableProps((prevState) => kaReducer(prevState, action));
  };

  // Notistack Configuration
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleDeleteColumn = (itemToDelete, titleOfItem) => {
    setIsConfirmDeleteColumn(true);
    setDeleteItemTitle({ titleOfItem, itemToDelete });
  };

  const handleDeleteColumnConf = async () => {
    const allMediaCenterJson = await getAllMediaCenterJson({
      getDataUrl: "/ivr/get/mediaCenterJson"
    });
    const { languageSelected, headerItems } = extractMediaCenterConfig(allMediaCenterJson);
    const filteredLanguageSelected = languageSelected.filter((item) => item.language !== deleteItemTitle.itemToDelete)

    // Collect all audio files to be deleted
    const audioFilesToDelete = headerItems.flatMap(item =>
      item.properties.filter(prop => prop.language === deleteItemTitle.itemToDelete)
    );

    // Delete audio files
    for (const audioFile of audioFilesToDelete) {
      if (audioFile.audioFileId && audioFile.audioFileName) {
        try {
          await deleteAudioFile({
            path: "/ivr/deleteFile",
            content: {
              language: audioFile.audioFileId,
              fileName: audioFile.audioFileName
            }
          });
        } catch (error) {
          console.error('Error deleting audio file:', error);
          // Consider how you want to handle errors (e.g., continue with other deletions, show a notification)
        }
      }
    }

    const filteredHeaderItems = headerItems.map((item) => ({
      ...item,
      properties: item.properties.filter((prop) => prop.language !== deleteItemTitle.itemToDelete)
    }));

    await saveMediaCenterJson({
      path: "/ivr/save/mediaCenterJson",
      content: {
        languageSelected: filteredLanguageSelected,
        headerItems: filteredHeaderItems
      }
    });

    setColumns((prevColumns) =>
      prevColumns.filter((item) => item.key !== deleteItemTitle.itemToDelete)
    );

    // Fetch the full list of languages
    const getLanguageList = await getLanguages({
      getDataUrl: "/ivr/languages"
    });

    // Update the languageList to include only languages not in use
    const languageData = getLanguageList?.data || [];
    const updatedLanguageList = languageData.filter(lang =>
      !filteredLanguageSelected.some(selected => selected.language === lang.language)
    );
    setLanguageList(updatedLanguageList);

    setDeleteItemTitle("");
    setIsConfirmDeleteColumn(false);
    enqueueSnackbar("Column and associated audio files deleted successfully", {
      variant: "success",
      anchorOrigin: { horizontal: "right", vertical: "bottom" },
      action: (key) => (
        <Button
          sx={{ color: "#FFF" }}
          size="small"
          onClick={() => closeSnackbar(key)}
        >
          Dismiss
        </Button>
      ),
    });
  };

  const handleEditColumnName = (itemToEdit, titleOfItem) => {
    setDeleteItemTitle({ titleOfItem, itemToEdit });
    setIsEditColumnName(true);
  };
  const handleInputColumnName = (e) => {
    setEditedColumnName(e.currentTarget.value);
  };

  const handleSaveColumn = async (selectedLanguages) => {
    const allMediaCenterJson = await getAllMediaCenterJson({
      getDataUrl: "/ivr/get/mediaCenterJson"
    });
    const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);

    // Add new languages to languageSelected
    const updatedLanguageSelected = [
      ...languageSelected,
      ...selectedLanguages.map(lang => ({ id: uuidv4(), language: lang.language }))
    ];

    // Update headerItems to include new language properties
    const updatedHeaderItems = headerItems.map(item => ({
      ...item,
      properties: [
        ...item.properties,
        ...selectedLanguages.map(lang => ({
          id: uuidv4(),
          language: lang.language,
          audioFileId: '',
          audioFileName: '',
          text: '',
          isServerGenerated: false
        }))
      ]
    }));

    // Save updated JSON
    await saveMediaCenterJson({
      path: "/ivr/save/mediaCenterJson",
      content: {
        languageSelected: updatedLanguageSelected,
        headerItems: updatedHeaderItems
      }
    });

    // Update columns state
    const newColumns = selectedLanguages.map(lang => ({
      key: lang.language,
      title: lang.language,
      width: 90
    }));

    setColumns(prevColumns => [
      ...prevColumns.slice(0, -1), // Remove the last column (delete column)
      ...newColumns,
      prevColumns[prevColumns.length - 1] // Add back the delete column
    ]);

    // Update rows state
    setRows(prevRows => prevRows.map(row => ({
      ...row,
      properties: [
        ...row.properties,
        ...selectedLanguages.map(lang => ({
          id: uuidv4(),
          language: lang.language,
          audioFileId: '',
          audioFileName: '',
          text: '',
          isServerGenerated: false
        }))
      ]
    })));

    // Update tableProps
    changeTableProps(prevState => ({
      ...prevState,
      columns: [
        ...prevState.columns.slice(0, -1),
        ...newColumns,
        prevState.columns[prevState.columns.length - 1]
      ],
      editableCells: [
        ...prevState.editableCells,
        ...rows.flatMap(row =>
          newColumns.map(col => ({ columnKey: col.key, rowKeyValue: row.id }))
        )
      ]
    }));

    // Update languageList
    setLanguageList(prevList =>
      prevList.filter(lang => !selectedLanguages.some(selected => selected.language === lang.language))
    );

    enqueueSnackbar("Languages added successfully", {
      variant: "success",
      anchorOrigin: { horizontal: "right", vertical: "bottom" },
      autoHideDuration: 3000,
    });
  };

  const handleSaveGroup = async () => {
    const allMediaCenterJson = await getAllMediaCenterJson({
      getDataUrl: "/ivr/get/mediaCenterJson"
    });
    const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);
      const columnsAccordingToSelectedLanguage = [
        { id: uuidv4(), language: DEFAULT_LANGUAGE_KEY },
        ...languageSelected
          .filter((item) => item.language !== DEFAULT_LANGUAGE_KEY)
          .map((item) => ({
      id: item.id,
      language: item.language,
        }))
      ];

    if (createName !== "" && createGroup !== "") {
      const newRow = {
        id: uuidv4(),
        type: createGroup,
        name: createName, // Use 'name' instead of 'rowName'
        requireLanguageWiseFiles: requireLanguageWiseFiles,
        properties: columnsAccordingToSelectedLanguage.map(lang => ({
          ...lang,
          audioFileId: '',
          audioFileName: '',
          text: '',
          isServerGenerated: false
        }))
      };

      await saveMediaCenterJson({
        path: "/ivr/save/mediaCenterJson",
        content: {
          languageSelected: languageSelected,
          headerItems: [...headerItems, newRow]
        }
      });

      setRows(prevRows => [...prevRows, newRow]);

      changeTableProps((prevState) => {
        const newEditableCells = prevState.columns
          .filter(column => column.key !== ":delete" && column.key !== "name" && column.key !== "type")
          .map((column) => ({
            columnKey: column.key,
            rowKeyValue: newRow.id,
          }));

        return {
          ...prevState,
          data: [...prevState.data, newRow],
          editableCells: [...prevState.editableCells, ...newEditableCells],
        };
      });

      // Reset the input fields
      setCreateGroup("");
      setCreateName("");
      setRequireLanguageWiseFiles(true);
      setOpenCreateTableDiologue(false);
      setIsSelectExistingRow(false);
      setIsSelectNewRow(false);
      enqueueSnackbar("Row created successfully", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        action: (key) => (
          <Button
            sx={{ color: "#FFF" }}
            size="small"
            onClick={() => closeSnackbar(key)}
          >
            Dismiss
          </Button>
        ),
      });
    } else {
      enqueueSnackbar("Please fill the input fields", {
        variant: "warning",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        action: (key) => (
          <Button
            sx={{ color: "#FFF" }}
            size="small"
            onClick={() => closeSnackbar(key)}
          >
            Dismiss
          </Button>
        ),
      });
    }
  };
  const handleNewData = () => {
    setIsSelectNewRow(true);
    setOpenCreateTableDiologue(true);
  };
  const handleClickOpen = () => {
    setOpenCreateTableDiologue(true);
  };
  const handleSearchText = (e) => {
    dispatch(search(e.currentTarget.value));
    setSearchText(e.currentTarget.value);
  };
  const handleOpenSearchDiologue = (e) => {
    setIsShowSearchBox(true);
  };
  const handleCloseSearchDiologue = (e) => {
    setIsShowSearchBox(false);
  };
  const handleClearSearchText = () => {
    setSearchText("");
    dispatch(search(""));
  };

  const handleAudioPlay = (audioId) => {
    if (currentlyPlayingId && currentlyPlayingId !== audioId) {
      // Stop the currently playing audio
      const audioElement = document.querySelector(`audio[data-audio-id="${currentlyPlayingId}"]`);
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    }
    setCurrentlyPlayingId(audioId);
  };

  const handleNameCellEdit = async (rowKeyValue, newValue) => {
    try {
      const allMediaCenterJson = await getAllMediaCenterJson({
        getDataUrl: "/ivr/get/mediaCenterJson"
      });

      const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);

      // Update the name in headerItems
      const updatedHeaderItems = headerItems.map(item => {
        if (item.id === rowKeyValue) {
          return {
            ...item,
            name: newValue
          };
        }
        return item;
      });

      // Save the updated JSON
      await saveMediaCenterJson({
        path: "/ivr/save/mediaCenterJson",
        content: {
          languageSelected,
          headerItems: updatedHeaderItems
        }
      });

      // Update local state
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === rowKeyValue ? { ...row, name: newValue } : row
        )
      );

      dispatch(updateCellValue(rowKeyValue, 'name', newValue));

      enqueueSnackbar("Name updated successfully", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Error updating name:', error);
      enqueueSnackbar("Error updating name", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 3000,
      });
    }
  };

  const handleConfirmDeleteRow = async () => {
    try {
      const allMediaCenterJson = await getAllMediaCenterJson({
        getDataUrl: "/ivr/get/mediaCenterJson"
      });

      const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);

      // Find the row to delete
      const rowToDelete = headerItems.find(item => item.id === deleteRowDetails.rowKeyValue);

      // Delete associated audio files
      if (rowToDelete) {
        for (const property of rowToDelete.properties) {
          if (property.audioFileId && property.audioFileName) {
            try {
              await deleteAudioFile({
                path: "/ivr/deleteFile",
                content: {
                  language: property.audioFileId,
                  fileName: property.audioFileName
                }
              });
            } catch (error) {
              console.error('Error deleting audio file:', error);
            }
          }
        }
      }

      // Filter out the deleted row
      const updatedHeaderItems = headerItems.filter(
        item => item.id !== deleteRowDetails.rowKeyValue
      );

      // Save the updated JSON
      await saveMediaCenterJson({
        path: "/ivr/save/mediaCenterJson",
        content: {
          languageSelected,
          headerItems: updatedHeaderItems
        }
      });

      // Update local state
      setRows(prevRows =>
        prevRows.filter(row => row.id !== deleteRowDetails.rowKeyValue)
      );

      dispatch(deleteRow(deleteRowDetails.rowKeyValue));
      setIsConfirmDeleteRow(false);
      setDeleteRowDetails({});

      enqueueSnackbar("Row deleted successfully", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Error deleting row:', error);
      enqueueSnackbar("Error deleting row", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 3000,
      });
    }
  };

  // console.log("columns", columns)
  // console.log("rows", rows);

  const handleSetDefaultLanguage = async (languageKey) => {
    try {
      const allMediaCenterJson = await getAllMediaCenterJson({
        getDataUrl: "/ivr/get/mediaCenterJson"
      });

      const { headerItems, languageSelected } = extractMediaCenterConfig(allMediaCenterJson);

      // Update languageSelected to set the new default and remove old default
      const updatedLanguageSelected = languageSelected.map(lang => ({
        ...lang,
        isDefault: lang.language === languageKey
      }));

      // Save the updated JSON
      await saveMediaCenterJson({
        path: "/ivr/save/mediaCenterJson",
        content: {
          languageSelected: updatedLanguageSelected,
          headerItems
        }
      });

      // Update all columns immediately to reflect the change
      setColumns(prevColumns => {
        return prevColumns.map(col => {
          const isCurrentLanguage = col.key === languageKey;

          if (col.key !== "name" && col.key !== ":delete" && col.key !== "type" && col.key !== DEFAULT_LANGUAGE_KEY) {
            return {
              ...col,
              title: (
                <Box sx={{
                  display: "flex",
                  gap: "5px",
                  textAlign: "center",
                  padding: '4px',
                  borderRadius: '4px'
                }}>
                  {isCurrentLanguage && <Star fontSize="small" sx={{ color: 'gold' }} />}
                  <Typography variant="p">{col.key}</Typography>
                  <DropDown
                    handleDelete={() => handleDeleteColumn(col.key, col.key)}
                    handleSetDefault={() => handleSetDefaultLanguage(col.key)}
                    isDefault={isCurrentLanguage}
                  />
                </Box>
              )
            };
          }
          return col;
        });
      });

      // Update the languageSelected state if you're maintaining it separately
      // setLanguageSelected(updatedLanguageSelected);

      enqueueSnackbar(`${languageKey} set as default language`, {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Error setting default language:', error);
      enqueueSnackbar("Error setting default language", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <div style={{ position: "relative", fontFamily: "Roboto" }}>
      {/* Toolbar */}
      <div style={{
        position: "absolute",
        top: "0",
        left: "0",
        right: "0",
        height: "68px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        zIndex: "1000",
        gap: "8px",
        paddingLeft: "8px",
        paddingRight: "8px"
      }}>
        {/* Left: icons + chips stacked */}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "1px" }}>
          {/* Line 1: Add Prompt, Add Language, Search */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <MediacenterDialogueConfig
              handleClickOpen={handleClickOpen}
              isSelectNewRow={isSelectNewRow}
              setIsSelectNewRow={setIsSelectNewRow}
              setIsSelectExistingRow={setIsSelectExistingRow}
              isSelectExistingRow={isSelectExistingRow}
              createGroup={createGroup}
              rows={rows}
              setCloseColumnDiologueBox={setCloseColumnDiologueBox}
              closeColumnDiologueBox={closeColumnDiologueBox}
              handleSaveGroup={handleSaveGroup}
              handleSaveColumn={handleSaveColumn}
              setCreateGroup={setCreateGroup}
              setCreateName={setCreateName}
              openCreateTableDiologue={openCreateTableDiologue}
              setOpenCreateTableDiologue={setOpenCreateTableDiologue}
              setColumnName={setColumnName}
              languageList={languageList}
              setLanguageList={setLanguageList}
              requireLanguageWiseFiles={requireLanguageWiseFiles}
              setRequireLanguageWiseFiles={setRequireLanguageWiseFiles}
            />

            {/* Filter/Funnel icon */}
            {rows.length > 0 && (
              <div style={{ display: "flex", alignItems: "center" }}>
                {searchText !== "" ? (
                  <Tooltip title="Clear Search" arrow placement="right">
                    <div style={{ cursor: "pointer" }}>
                      <IoFunnel
                        color="green"
                        fill="lightGreen"
                        onClick={handleClearSearchText}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip title="Search" arrow placement="right">
                    <div style={{ cursor: "pointer" }}>
                      <IoFunnel
                        style={{
                          color: darkMode ? "#fff" : "#1e1e1e",
                        }}
                        onClick={handleOpenSearchDiologue}
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
          {/* Line 2: Group filter chips */}
          {rows.length > 0 && (
            <div style={{ display: "flex", gap: "3px", alignItems: "center", paddingLeft: "2px" }}>
              {["all", "IVR", "MOH", "Others"].map((filter) => (
                <Chip
                  key={filter}
                  label={filter === "all" ? "All" : filter}
                  size="small"
                  variant={groupFilter === filter ? "filled" : "outlined"}
                  onClick={() => setGroupFilter(filter)}
                  sx={{
                    fontWeight: groupFilter === filter ? 600 : 400,
                    fontSize: "0.55rem",
                    height: "18px",
                    minWidth: "28px",
                    "& .MuiChip-label": { padding: "0 6px" },
                    backgroundColor: groupFilter === filter
                      ? (filter === "IVR" ? "#1976d2" : filter === "MOH" ? "#ed6c02" : filter === "Others" ? "#9c27b0" : (darkMode ? "#555" : "#424242"))
                      : "transparent",
                    color: groupFilter === filter
                      ? "#fff"
                      : (darkMode ? "#bbb" : "#666"),
                    borderColor: darkMode ? "#444" : "#ddd",
                    "&:hover": {
                      backgroundColor: groupFilter === filter
                        ? undefined
                        : (darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"),
                    },
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Actions Menu & TTS Badge - Right aligned */}
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* Failed TTS indicator badge */}
          {AuthenticationService.getTTS() === "true" && failedTTSItems && failedTTSItems.length > 0 && (
            <Tooltip
              title={`${failedTTSItems.length} TTS item(s) failed. Use menu to retry.`}
              arrow
              placement="left"
            >
              <Box
                sx={{
                  backgroundColor: "#ff9800",
                  color: "#ffffff",
                  padding: "4px 10px",
                  borderRadius: "12px",
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  cursor: "help"
                }}
              >
                <Warning sx={{ fontSize: "1rem" }} />
                {failedTTSItems.length} TTS Failed
              </Box>
            </Tooltip>
          )}

          {/* More actions menu */}
          <Tooltip title="More actions" arrow placement="left">
            <IconButton
              onClick={(e) => setMoreMenuAnchorEl(e.currentTarget)}
              sx={{
                color: darkMode ? "#fff" : "#1e1e1e",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <MoreVert />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={moreMenuAnchorEl}
            open={isMoreMenuOpen}
            onClose={() => setMoreMenuAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
                color: darkMode ? "#fff" : "#000",
                minWidth: "240px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
              }
            }}
          >
            {AuthenticationService.getTTS() === "true" && (
              <MenuItem
                disabled={isRetryingTTS || !failedTTSItems || failedTTSItems.length === 0}
                onClick={() => {
                  setMoreMenuAnchorEl(null);
                  handleRetryTTS();
                }}
              >
                <ListItemIcon>
                  {isRetryingTTS
                    ? <CircularProgress size={20} sx={{ color: "#ff9800" }} />
                    : <Refresh sx={{ color: "#ff9800" }} />}
                </ListItemIcon>
                <ListItemText
                  primary="Retry Failed TTS"
                  secondary={failedTTSItems?.length > 0 ? `${failedTTSItems.length} item(s) failed` : "No failed items"}
                  primaryTypographyProps={{ sx: { color: darkMode ? "#fff" : "#000" } }}
                  secondaryTypographyProps={{ sx: { color: darkMode ? "#aaa" : "#666", fontSize: "0.75rem" } }}
                />
              </MenuItem>
            )}

            <MenuItem
              disabled={isSyncing || rows.length === 0}
              onClick={() => {
                setMoreMenuAnchorEl(null);
                handleSyncSoundFiles();
              }}
            >
              <ListItemIcon>
                {isSyncing
                  ? <CircularProgress size={20} sx={{ color: "#1976d2" }} />
                  : <Sync sx={{ color: "#1976d2" }} />}
              </ListItemIcon>
              <ListItemText
                primary="Synchronise Media"
                secondary="Sync to other nodes"
                primaryTypographyProps={{ sx: { color: darkMode ? "#fff" : "#000" } }}
                secondaryTypographyProps={{ sx: { color: darkMode ? "#aaa" : "#666", fontSize: "0.75rem" } }}
              />
            </MenuItem>

            {isAdmin && <Divider sx={{ borderColor: darkMode ? "#444" : "#e0e0e0" }} />}

            {isAdmin && (
              <MenuItem
                disabled={isResetting}
                onClick={() => {
                  setMoreMenuAnchorEl(null);
                  setIsResetDialogOpen(true);
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: darkMode ? "rgba(211,47,47,0.15)" : "rgba(211,47,47,0.08)",
                  },
                }}
              >
                <ListItemIcon>
                  {isResetting
                    ? <CircularProgress size={20} sx={{ color: "#d32f2f" }} />
                    : <RestartAlt sx={{ color: "#d32f2f" }} />}
                </ListItemIcon>
                <ListItemText
                  primary="Reset Media Center"
                  secondary="Destructive — deletes all media"
                  primaryTypographyProps={{ sx: { color: "#d32f2f", fontWeight: "bold" } }}
                  secondaryTypographyProps={{ sx: { color: darkMode ? "#ff8a80" : "#d32f2f", fontSize: "0.75rem" } }}
                />
              </MenuItem>
            )}
          </Menu>
        </div>
      </div>

      {/* Search box */}
      {isShowSearchBox && (
        <SearchBar
          handleSearchText={handleSearchText}
          onClose={handleCloseSearchDiologue}
          searchText={searchText}
          isShowSearchBox={isShowSearchBox}
          setIsShowSearchBox={setIsShowSearchBox}
        />
      )}

      {rows.length > 0 && (
        <>
          <div className={darkMode ? "darkTheme" : "lightTheme"} style={{ paddingTop: "11px" }}>
            <Table
              {...tableProps}
              dispatch={dispatch}
              columns={columns}
              rowKeyField="id"
              groups={[{ columnKey: "type" }]}
              childComponents={{
                cell: {
                  content: (props) => {
                    if (props.column.key === "name") {
                      const isEditing = editingRowId === props.rowData.id;
                      return isEditing ? (
                        <input
                          style={{
                            width: '100%',
                            backgroundColor: darkMode ? '#1e1e1e' : '#FFFFFF',
                            border: '1px solid #1A73E8',
                            borderRadius: '4px',
                            color: darkMode ? 'white' : 'black',
                            padding: '5px',
                            outline: 'none',
                            boxShadow: '0 0 0 2px rgba(26, 115, 232, 0.1)',
                            transition: 'all 0.2s ease-in-out'
                          }}
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <div style={{
                          padding: '5px',
                          width: '100%',
                          height: '100%'
                        }}>
                          {props.value}
                        </div>
                      );
                    }
                    if (props.column.key !== "type" && props.column.key !== ":delete") {
                      return (
                        <CustomUploader
                          {...props}
                          rowData={props.rowData}
                          currentlyPlayingId={currentlyPlayingId}
                          onAudioPlay={handleAudioPlay}
                          isRowEditing={editingRowId === props.rowData.id}
                          failedTTSItems={failedTTSItems}
                          setFailedTTSItems={setFailedTTSItems}
                          onRowsUpdate={setRows}
                        />
                      );
                    }
                    return props.value;
                  },
                },
                noDataRow: {
                  content: () =>
                    rows.length === 0 ? (
                      "No Data Found"
                    ) : (
                      <h2 style={{ color: "#fff" }}>No Data Found</h2>
                    ),
                },
                dataRow: {
                  elementAttributes: ({ rowData }) => ({
                    style: {
                      backgroundColor: darkMode ? "#1e1e1e" : "#FFFFFF",
                      color: darkMode ? "white" : "black",
                    },
                  }),
                },
                cellText: {
                  content: (props) => {
                    switch (props.column?.key) {
                      case ":delete":
                        return (
                          <DeleteRowComponent
                            rowKeyValue={props.rowKeyValue}
                            deleteItemName={props.rowData.name}
                            isEditing={editingRowId === props.rowKeyValue}
                            isSystemPrompt={props.rowData.isSystemPrompt}
                            onEditClick={handleEditClick}
                            onSaveClick={handleSaveClick}
                            onDeleteClick={(rowKeyValue, deleteItemName) => {
                              setDeleteRowDetails({ rowKeyValue, deleteItemName });
                              setIsConfirmDeleteRow(true);
                            }}
                          />
                        );
                      default:
                        return props.value;
                    }
                  },
                  elementAttributes: ({ rowData }) => ({
                    style: {
                      color: darkMode ? "white" : "black",
                    },
                  }),
                },
                groupCell: {
                  elementAttributes: () => ({
                    style: {
                      backgroundColor: darkMode ? "#121212" : "#FAFAFA",
                      color: darkMode ? "#fff" : "#000",
                      border: darkMode
                        ? "2px solid #1e1e1e"
                        : "2px solid #f1f1f1",
                    },
                  }),
                },

                groupExpandButton: {
                  elementAttributes: () => ({
                    style: {
                      fill: !darkMode ? "#000" : "white",
                    },
                  }),
                },
                headCell: {
                  elementAttributes: () => ({
                    style: {
                      backgroundColor: darkMode ? "#121212" : "#ffffff",
                      color: darkMode ? "#fff" : "#000",
                    },
                  }),
                },
              }}
            />
            {isConfirmDeleteColumn && (
              <AlertDelete
                name={deleteItemTitle.titleOfItem}
                handleConfirmDelete={handleDeleteColumnConf}
                isDelete={isConfirmDeleteColumn}
                setIsDelete={setIsConfirmDeleteColumn}
              />
            )}
            {isConfirmDeleteRow && (
              <AlertDelete
                name={deleteRowDetails.deleteItemName}
                handleConfirmDelete={handleConfirmDeleteRow}
                isDelete={isConfirmDeleteRow}
                setIsDelete={setIsConfirmDeleteRow}
              />
            )}
            {isEditColumnName && (
              <InputBox
                changeTableProps={changeTableProps}
                columns={columns}
                setColumns={setColumns}
                deleteItemTitle={deleteItemTitle}
                handleInputColumnName={handleInputColumnName}
                editedColumnName={editedColumnName}
                isEditColumnName={isEditColumnName}
                setEditedColumnName={setEditedColumnName}
                setIsEditColumnName={setIsEditColumnName}
                rows={rows}
                setRows={setRows}
              />
            )}
          </div>
        </>
      )}
      {rows.length === 0 && <EmptyContainer handleNewData={handleNewData} />}

      {/* Reset Media Center Confirmation Dialog */}
      <Dialog
        open={isResetDialogOpen}
        onClose={() => {
          if (!isResetting) {
            setIsResetDialogOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
            color: darkMode ? "#fff" : "#000",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <WarningAmberRoundedIcon sx={{ color: "#d32f2f", fontSize: "28px" }} />
          <MDTypography variant="h6" sx={{ color: darkMode ? "#fff" : "#000" }}>
            Reset Media Center
          </MDTypography>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
            paddingTop: "16px !important",
          }}
        >
          <MDTypography
            variant="body2"
            sx={{
              color: "#d32f2f",
              fontWeight: "bold",
              marginBottom: "12px",
            }}
          >
            This action is irreversible and will permanently destroy all media data.
          </MDTypography>
          <MDTypography
            variant="body2"
            sx={{ color: darkMode ? "#ccc" : "#555", marginBottom: "8px" }}
          >
            The following will be deleted:
          </MDTypography>
          <Box component="ul" sx={{ color: darkMode ? "#ccc" : "#555", pl: 2, mb: 2 }}>
            <li>All language audio folders and uploaded files</li>
            <li>Music on Hold (MOH) files</li>
            <li>Neutral/default media files</li>
            <li>Media Center configuration (mediaCenter.json)</li>
          </Box>
          <MDTypography
            variant="body2"
            sx={{ color: darkMode ? "#ccc" : "#555", marginBottom: "12px" }}
          >
            A clean folder structure and fresh configuration will be created.
          </MDTypography>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
            padding: "16px",
          }}
        >
          <MDBox display="flex" gap={2}>
            <MDButton
              variant="gradient"
              color="error"
              size="small"
              disabled={isResetting}
              onClick={handleResetMediaCenter}
            >
              {isResetting ? "Resetting..." : "Reset Media Center"}
            </MDButton>
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              disabled={isResetting}
              onClick={() => {
                setIsResetDialogOpen(false);
              }}
            >
              Cancel
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MediaCenterComponent;